import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A histogram is a type of bar chart used to represent the distribution of a dataset by
          grouping data points into bins or intervals. The height of each bar reflects the frequency
          or count of data points within each bin. Histograms are ideal for visualizing the
          distribution, spread, and frequency of continuous data, helping to identify patterns such
          as skewness, variability, or the presence of outliers.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Histograms are perfect for illustrating:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Distribution of Data:</strong> Histograms are ideal for visualizing
            the distribution of numerical data, showing how values are spread across different
            ranges or bins, such as age groups or test scores.
          </li>
          <li>
            <strong>Identifying Data Patterns:</strong> They help in identifying patterns, such as
            skewness, normal distribution, or outliers in datasets, making them valuable for
            statistical analysis and data exploration.
          </li>
          <li>
            <strong>Comparing Frequency Across Intervals:</strong> Histograms are useful for
            comparing the frequency or count of data points within specific intervals, such as the
            frequency of sales within different price ranges or time periods.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Histograms are highly effective for visualizing data distributions, but they have some
          notable limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Dependency on Bin Size and Range:</strong> The choice of bin size and range can
            significantly impact the visual representation, potentially leading to misleading
            interpretations if not carefully selected.
          </li>
          <li>
            <strong>Inability to Show Individual Data Points:</strong> Histograms aggregate data
            into bins, which means specific data values are obscured, making it impossible to
            analyze individual observations.
          </li>
          <li>
            <strong>Limited Comparability:</strong> Comparing multiple datasets within the same
            histogram can be challenging, as overlapping bars or too many bins may clutter the
            visualization and reduce clarity.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in histograms plays a key role in enhancing data visualization and
          clarity. Distinct colors for different bins help differentiate data ranges, making it
          easier to compare the frequency of values within specific intervals. Gradients or subtle
          color shifts can indicate intensity, with darker or more vibrant colors representing
          higher frequencies. Careful use of color can also help emphasize specific trends, such as
          outliers or clusters of data, ensuring the histogram is both visually appealing and
          informative.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Histograms are useful for displaying the distribution of data, but they come with specific
          challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Bin Size Sensitivity:</strong> The choice of bin size can significantly affect
            the interpretation of the data. Too large or too small bins may obscure important trends
            or introduce noise. differences.
          </li>
          <li>
            <strong>Overlapping Data Ranges:</strong> When data has a wide range or multiple peaks,
            histograms can become cluttered or difficult to read, especially if the bin intervals
            overlap.
          </li>
          <li>
            <strong>Not Ideal for Small Data Sets:</strong> Histograms are less effective with small
            data sets, as they may not provide enough data points to create meaningful or accurate
            distributions.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
